import classNames from "classnames"
import React from "react"
import type { CSSProperties, ReactNode } from "react"

type SizeToken = "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl"

const SIZES: Record<SizeToken, { className: string; style?: CSSProperties }> = {
  xs: { className: "text-xs leading-4" },
  sm: { className: "text-sm leading-6" },
  md: { className: "text-base leading-5" },
  lg: { className: "text-lg leading-8" },
  xl: { className: "text-2xl leading-8" },
  "2xl": { className: "leading-10", style: { fontSize: "2rem" } },
  "3xl": { className: "text-5xl leading-14" }
}

type TextWeight = "light" | "normal" | "semibold"

const WEIGHT_CLASSES: Record<TextWeight, string> = {
  light: "font-light",
  normal: "font-normal",
  semibold: "font-semibold"
}

type TextColor =
  | "black"
  | "gray-600"
  | "gray-700"
  | "gray-800"
  | "red-400"
  | "red-500"

const COLOR_CLASSES: Record<TextColor, string> = {
  black: "text-black",
  "gray-600": "text-gray-600",
  "gray-700": "text-gray-700",
  "gray-800": "text-gray-800",
  "red-400": "text-red-400",
  "red-500": "text-red-500"
}

const hasCSSUnit = (size: string) => /px|r?em|%$/.test(size)

const getSize = (
  size: string
): { className?: string; style?: CSSProperties } | undefined => {
  if (hasCSSUnit(size)) return { style: { fontSize: size } }
  return SIZES[size as SizeToken]
}

export type TextProps = {
  /** HTML tag to render the text in (e.g. "p", "span", "label") */
  as?: string
  /** Additional CSS class names */
  className?: string
  /** Content to render inside the text element */
  children?: ReactNode
  /** Text size — a design token or a custom CSS value (e.g. "1.5rem") */
  size?: SizeToken | (string & {})
  /** Font weight */
  weight?: TextWeight
  /** Text color */
  color?: TextColor
  /** Inline styles */
  style?: CSSProperties
  /** Associates a label with a form element */
  htmlFor?: string
  /** Element ID */
  id?: string
  /** ARIA live region behavior */
  "aria-live"?: "off" | "polite" | "assertive"
  /** Whether the entire region should be announced */
  "aria-atomic"?: boolean | "true" | "false"
  /** Test ID for targeting in tests */
  "data-testid"?: string
}

/**
 * A flexible text component for rendering styled text with configurable size, weight, and color.
 * Supports design token sizes as well as custom CSS values.
 */
export default function Text({
  as: Component = "div",
  className,
  children,
  size = "md",
  weight,
  color,
  style,
  htmlFor,
  id,
  "aria-live": ariaLive,
  "aria-atomic": ariaAtomic,
  "data-testid": dataTestId
}: TextProps) {
  const sizeProps = getSize(size)

  className = classNames(
    className,
    sizeProps?.className,
    weight && WEIGHT_CLASSES[weight],
    color && COLOR_CLASSES[color]
  )

  const mergedStyle = { ...sizeProps?.style, ...style }
  const Tag = Component as React.ElementType

  return (
    <Tag
      className={className}
      style={mergedStyle}
      htmlFor={htmlFor}
      id={id}
      aria-live={ariaLive}
      aria-atomic={ariaAtomic}
      data-testid={dataTestId}
    >
      {children}
    </Tag>
  )
}

export { Text }
